import { atom } from "jotai";
import { getManualInstrumentType, knownStatusList, type TStatus } from "./statusLogic";
import { useCallback } from "react";

import { monthRowIds, offsetToRow } from "@package/grid-utils";
import {
  type TProduct,
  useUpdateGridSettings,
  useGridSettingsStatusMap,
  useGlobalPackagesByProductIds,
  useGlobalProductsByProductIds,
} from "../../../data";
import { LuDownload } from "react-icons/lu";
import { type TOptimisticCell, useCalcs, type Calcs } from "../../calcs/react";
import type { TStatusOption } from "./consts";
import { sourceToGlobalPackageIcons } from "./consts";
import type { ProductInfoFragmentFragment } from "../../../__generated__/gql/graphql";

export const changeAllHoverAtom = atom<boolean>(false);

export function useUpdateColumnStatus() {
  const statusMap = useGridSettingsStatusMap();
  const { optimistic: updateOptimistic } = useUpdateGridSettings();

  return useCallback(
    (toStatusMap: Record<string, TStatus>, productIds: string[]) => {
      const status_map = { ...statusMap };
      productIds.forEach((productId) => {
        status_map[productId] = toStatusMap[productId];
      });
      return updateOptimistic({ status_map });
    },
    [statusMap, updateOptimistic],
  );
}

async function writeStatusInstruments(
  { to, curveId }: { to: TStatus; curveId: string },
  getData: Calcs["getGrid"],
  updateCell: Calcs["cellEdit"],
) {
  const storageType = getManualInstrumentType(to);
  if (!storageType || !knownStatusList.includes(to)) {
    console.error(`can't write when going to status: ${to}`);
    return;
  }

  const rowIds = monthRowIds.map((_, idx) => idx);

  const result = await getData([[curveId, "value"]], rowIds);

  const edits: TOptimisticCell[] = rowIds.map((rowId, idx) => ({
    result: result?.[0]?.[idx]?.Ok ?? null,
    month: offsetToRow[rowId],
    storageType,
    offset: rowId,
    product: curveId,
    field: "value",
    rowId: monthRowIds[idx],
    force: true, // Force write even if value is the same
    edited_at: Date.now(),
  }));

  await updateCell(...edits);
}

export function useWriteStatusInstruments() {
  const calcs = useCalcs();
  return useCallback(
    async (params: Parameters<typeof writeStatusInstruments>[0]) =>
      writeStatusInstruments({ ...params }, calcs.getGrid, calcs.cellEdit),
    [calcs],
  );
}

export function useGlobalDependencyOptions(
  products: TProduct[] | readonly ProductInfoFragmentFragment[] | null | undefined,
) {
  const dependencies = products?.flatMap((product) => product.product_global_dependencies) || [];

  const { data: globalProducts } = useGlobalProductsByProductIds(dependencies.map((dep) => dep.global_product));
  const { data: globalDependenciesDetails } = useGlobalPackagesByProductIds(
    dependencies.map((dep) => dep.global_product),
  );
  if (!products || !globalDependenciesDetails) return [];

  const uniqueOptions = new Set<number>();

  return dependencies.reduce<TStatusOption[]>((acc, dep) => {
    const productId = dep.global_product;
    const productDetail = globalProducts?.find((prod) => prod.id === productId);
    const globalPackageId = productDetail?.global_package;
    const globalPackageDetail = globalDependenciesDetails.find((pkg) => pkg.id === globalPackageId);

    const iconFileName = globalPackageDetail?.source
      ? sourceToGlobalPackageIcons[globalPackageDetail.source]
      : undefined;
    const iconPath = iconFileName ? `/icons/${iconFileName}` : undefined;

    const option = {
      value: productId,
      label: "",
      style: { display: "flex", gap: "3px" },
      icon: LuDownload,
      image: false,
      logo: iconPath || undefined,
    } satisfies TStatusOption;

    if (globalPackageDetail?.source && !uniqueOptions.has(globalPackageDetail.source)) {
      uniqueOptions.add(globalPackageDetail.source);
      acc.push(option);
    }

    return acc;
  }, []);
}
